import React, { useState, useEffect } from 'react';
import { Quote } from 'lucide-react';
import { DateTime } from './DateTime';
import { ShareButton } from '../shared/ShareButton';
import toast from 'react-hot-toast';

// Default verse as fallback
const DEFAULT_VERSE = {
  reference: 'Philippians 4:13',
  text: 'I can do all things through Christ which strengtheneth me.'
};

// Cache duration reduced to 6 hours to ensure more frequent updates
const CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

interface CachedVerse {
  verse: {
    reference: string;
    text: string;
  };
  timestamp: number;
}

export function VerseOfDay() {
  const [verse, setVerse] = useState<{ reference: string; text: string } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDailyVerse();
  }, []);

  const loadDailyVerse = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Check cache and validate timestamp
      const cached = localStorage.getItem('verse_of_day');
      if (cached) {
        const { verse, timestamp }: CachedVerse = JSON.parse(cached);
        const now = Date.now();
        
        // Use cache only if it's less than CACHE_DURATION old
        if (now - timestamp < CACHE_DURATION) {
          setVerse(verse);
          setIsLoading(false);
          return;
        }
      }
      
      // Cache expired or doesn't exist, fetch new verse from local Bible data
      const randomVerse = await getRandomVerseFromBible();
      
      if (!randomVerse) throw new Error('No verse received');

      // Cache the new verse with timestamp
      localStorage.setItem('verse_of_day', JSON.stringify({
        verse: randomVerse,
        timestamp: Date.now()
      }));

      setVerse(randomVerse);
    } catch (err) {
      console.error('Error fetching verse of the day:', err);
      setError('Unable to load verse of the day');
      // Use default verse as fallback
      setVerse(DEFAULT_VERSE);
      toast.error('Using default verse due to service error');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to get a random verse from the Bible data
  const getRandomVerseFromBible = async (): Promise<{ reference: string; text: string } | null> => {
    try {
      // List of books to randomly select from
      const books = [
        'Genesis', 'Psalms', 'Proverbs', 'Isaiah', 'Matthew', 
        'John', 'Romans', 'Ephesians', 'Philippians', 'Colossians'
      ];
      
      // Select a random book
      const randomBook = books[Math.floor(Math.random() * books.length)];
      
      // Fetch the book data
      const response = await fetch(`/bibles/English/${randomBook}.json`);
      if (!response.ok) throw new Error(`Failed to load book: ${randomBook}`);
      
      const bookData = await response.json();
      const book = bookData[randomBook];
      
      if (!book) throw new Error(`Book ${randomBook} not found in data`);
      
      // Get available chapters
      const chapters = Object.keys(book);
      const randomChapter = chapters[Math.floor(Math.random() * chapters.length)];
      
      // Get verses for the selected chapter
      const verses = book[randomChapter];
      if (!verses || !Array.isArray(verses) || verses.length === 0) {
        throw new Error(`No verses found in ${randomBook} ${randomChapter}`);
      }
      
      // Select a random verse
      const randomVerseIndex = Math.floor(Math.random() * verses.length);
      const text = verses[randomVerseIndex];
      const reference = `${randomBook} ${randomChapter}:${randomVerseIndex + 1}`;
      
      return { reference, text };
    } catch (error) {
      console.error('Error getting random verse:', error);
      return null;
    }
  };

  // Function to force refresh the verse
  const refreshVerse = async () => {
    // Remove from cache
    localStorage.removeItem('verse_of_day');
    // Reload
    await loadDailyVerse();
    toast.success('Verse refreshed successfully!');
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-3xl mx-auto p-3 md:p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg animate-pulse">
        <div className="h-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (error && !verse) {
    return (
      <div className="w-full max-w-3xl mx-auto p-3 md:p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
        <div className="text-center text-gray-600 dark:text-gray-300">
          {error}
        </div>
      </div>
    );
  }

  if (!verse) return null;

  return (
    <div className="w-full max-w-3xl mx-auto p-3 md:p-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start gap-2 mb-2">
        <DateTime />
        <div className="flex items-center space-x-2 sm:space-x-4">
          <div className="text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 font-medium">
            Verse of the Day
          </div>
          <button
            onClick={refreshVerse}
            className="text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-md hover:bg-indigo-200 dark:hover:bg-indigo-800/30"
          >
            Refresh
          </button>
          <ShareButton
            title="Bible Me - Verse of the Day"
            text={`📖 Verse of the Day:\n\n"${verse.text}"\n\n- ${verse.reference}`}
            url={`https://verse4all.pw/bible?ref=${encodeURIComponent(verse.reference)}`}
          />
        </div>
      </div>
      <div className="flex items-start space-x-2 sm:space-x-3">
        <Quote className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-1" />
        <div className="flex-1 min-w-0">
          <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 italic mb-1 sm:mb-2 line-clamp-3">
            "{verse.text}"
          </p>
          <p className="text-right text-xs sm:text-sm text-indigo-600 dark:text-indigo-400 font-semibold">
            - {verse.reference}
          </p>
        </div>
      </div>
    </div>
  );
}